import {Plural, Trans} from '@lingui/macro';
import {flatten} from 'lodash';
import React, {useEffect, useState} from 'react';

import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useUnmountCancelToken} from '../../../../srcCommon/helpers/hookHelpers';
import {computeIsGlobalNamespace} from '../../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {
  ApiRuleBundles,
  RuleBundleModelStrict,
  RuleBundlePrioritiesEnum,
} from '../../../models/api/ruleBundleModel';
import {RuleModelStrict} from '../../../models/api/ruleModel';
import {fetchApiAsync} from '../../../services/http/http';
import {getFlatChannels} from '../../../store/data/channels/channelsSelectors';
import {getCompany} from '../../../store/data/company/companySelectors';
import {getShouldRulesWrapTriggerConditions} from '../../../store/featureFlags/featureFlagsSelectors';
import {useBaseEditorContext} from '../../editors/baseEditorContext';
import {isRegularRuleBundle} from '../ruleBundleHelpers';
import {
  actionsCanTriggerRules,
  canBeTriggeredByRules,
  inboxesImpactedByCompoundConditions,
} from '../ruleSimulationHelpers';
import {ImpactedInboxesDialog} from './dialog/impactedInboxesDialog';
import {TriggeredRulesDialog} from './dialog/triggeredRulesDialog';
import {TriggeringRulesDialog} from './dialog/triggeringRulesDialog';
import {useAllInboxes} from './hooks/useAllInboxes';
import {
  StyledRuleEditorSidebarSection,
  StyledRuleEditorSidebarSectionList,
  StyledRuleEditorSidebarSectionListHighlight,
  StyledRuleEditorSidebarSectionTitle,
} from './ruleEditorSidebarSection';
import {RuleEditorState} from './state/ruleEditorState';

/*
 * Types.
 */

interface RuleEditorImpactProps {
  ruleEditorState: RuleEditorState;
}

enum ImpactDialogsEnum {
  IMPACTED_INBOXES = 'impacted_inboxes',
  TRIGGERED_RULES = 'triggered_rules',
  TRIGGERING_RULES = 'triggering_rules',
}

/*
 * Component.
 */

export const RuleEditorImpact = ({ruleEditorState}: RuleEditorImpactProps) => {
  const theme = useUserTheme();
  const [dialog, setDialog] = useState<ImpactDialogsEnum | null>(null);
  const [allRules, setAllRules] = useState<ReadonlyArray<RuleModelStrict>>([]);
  const cancelToken = useUnmountCancelToken();
  const {namespace} = useBaseEditorContext();
  const shouldWrapTriggerConditions = useAppSelector(getShouldRulesWrapTriggerConditions);
  const company = useAppSelector(getCompany);
  const inboxes = useAllInboxes();
  const channels = useAppSelector(getFlatChannels);
  const rule = RuleEditorState.toRuleWithBundle(ruleEditorState, namespace, shouldWrapTriggerConditions);
  const impactInboxes = computeIsGlobalNamespace(namespace)
    ? inboxes
    : inboxes.filter((i) => i.namespace === namespace);
  const {impactedInboxes} = inboxesImpactedByCompoundConditions(
    rule.compoundConditions,
    impactInboxes,
    channels,
  );
  const triggeredRules = actionsCanTriggerRules(allRules, rule, inboxes);
  const triggeringRules = canBeTriggeredByRules(allRules, rule, inboxes);
  const hasImpact = impactedInboxes.length > 0 || triggeredRules.length > 0 || triggeringRules.length > 0;

  useEffect(() => {
    fetchApiAsync(
      ApiRuleBundles,
      `${company.url}/namespaces/${namespace}/rule_bundles?priority=${RuleBundlePrioritiesEnum.DEFAULT}`,
      {
        cancelToken,
      },
    ).then((bundles) => {
      setAllRules(
        flatten(
          (bundles as ReadonlyArray<RuleBundleModelStrict>)
            .filter(isRegularRuleBundle)
            .map(({rules, ...bundle}) => rules.map((r) => ({...r, bundle}))),
        ).filter((r) => r.id !== rule.id),
      );
    });
  }, []);

  if (!hasImpact) {
    return null;
  }

  function maybeRenderImpactedInboxes() {
    if (!impactedInboxes.length) {
      return null;
    }

    return (
      <li>
        <Trans id="rule-editor-sidebar-impact-inbox-title">Affects conversations in</Trans>
        <br />
        <StyledRuleEditorSidebarSectionListHighlight
          onClick={() => setDialog(ImpactDialogsEnum.IMPACTED_INBOXES)}
        >
          {impactedInboxes.length}{' '}
          <Plural
            id="rule-editor-sidebar-impact-inbox-value"
            value={impactedInboxes.length}
            one="inbox"
            other="inboxes"
          />
        </StyledRuleEditorSidebarSectionListHighlight>
      </li>
    );
  }

  function maybeRenderTriggeringRules() {
    if (!triggeringRules.length) {
      return null;
    }

    return (
      <li>
        <Trans id="rule-editor-sidebar-impact-triggering-title">May be triggered by</Trans>{' '}
        <StyledRuleEditorSidebarSectionListHighlight
          onClick={() => setDialog(ImpactDialogsEnum.TRIGGERING_RULES)}
        >
          {triggeringRules.length}{' '}
          <Plural
            id="rule-editor-sidebar-impact-triggering-value"
            value={triggeringRules.length}
            one="rule"
            other="rules"
          />
        </StyledRuleEditorSidebarSectionListHighlight>
      </li>
    );
  }

  function maybeRenderTriggeredRules() {
    if (!triggeredRules.length) {
      return null;
    }

    return (
      <li>
        <Trans id="rule-editor-sidebar-impact-trigger-title">May trigger</Trans>{' '}
        <StyledRuleEditorSidebarSectionListHighlight
          onClick={() => setDialog(ImpactDialogsEnum.TRIGGERED_RULES)}
        >
          {triggeredRules.length}{' '}
          <Plural
            id="rule-editor-sidebar-impact-trigger-value"
            value={triggeredRules.length}
            one="rule"
            other="rules"
          />
        </StyledRuleEditorSidebarSectionListHighlight>
      </li>
    );
  }

  function maybeRenderDialog() {
    if (dialog === ImpactDialogsEnum.IMPACTED_INBOXES) {
      return <ImpactedInboxesDialog onCancel={() => setDialog(null)} inboxes={impactedInboxes} rule={rule} />;
    }

    if (dialog === ImpactDialogsEnum.TRIGGERED_RULES) {
      return <TriggeredRulesDialog onCancel={() => setDialog(null)} rules={triggeredRules} rule={rule} />;
    }

    if (dialog === ImpactDialogsEnum.TRIGGERING_RULES) {
      return <TriggeringRulesDialog onCancel={() => setDialog(null)} rules={triggeringRules} rule={rule} />;
    }

    return null;
  }

  return (
    <StyledRuleEditorSidebarSection $color={theme.palette.blue.shade10}>
      <StyledRuleEditorSidebarSectionTitle>
        <Icon name="info" />
        <Trans id="rule-editor-sidebar-impact">Impact of this rule</Trans>
      </StyledRuleEditorSidebarSectionTitle>

      <StyledRuleEditorSidebarSectionList>
        {maybeRenderImpactedInboxes()}
        {maybeRenderTriggeringRules()}
        {maybeRenderTriggeredRules()}
        {maybeRenderDialog()}
      </StyledRuleEditorSidebarSectionList>
    </StyledRuleEditorSidebarSection>
  );
};
